// Karplus-Strong plucked-string synthesis via Web Audio API.
// No external dependencies — generates classical-guitar-like tones (nylon strings, fingerstyle
// pluck) from noise + filtered delay, played through a shared "body" filter chain that
// approximates the instrument's resonance.
import { DEFAULT_VOLUME, gainForVolume } from "./volume";

// Standard tuning open-string frequencies (Hz)
// String 6 (low E) = E2, String 5 = A2, String 4 = D3, String 3 = G3, String 2 = B3, String 1 = E4
const OPEN_STRING_FREQUENCIES: Record<number, number> = {
  6: 82.41, // E2
  5: 110.0, // A2
  4: 146.83, // D3
  3: 196.0, // G3
  2: 246.94, // B3
  1: 329.63, // E4
};

const SEMITONES_PER_OCTAVE = 12;

/** How long a pluck rings, and how fast the string damps per period of the feedback loop. */
interface PluckCharacter {
  durationSeconds: number;
  decay: number;
}

// A normal pluck rings for a few seconds, then fades to silence.
const PLUCK: PluckCharacter = { durationSeconds: 3.0, decay: 0.998 };
// "Let ring": lighter damping and a long tail, so notes tapped one after another keep
// sounding together like an undamped chord instead of each fading before the next lands.
const RINGING_PLUCK: PluckCharacter = { durationSeconds: 8.0, decay: 0.9995 };
// Starting amplitude of the gain envelope, and the near-zero level it ramps down to.
const PLUCK_GAIN = 0.3;
const SILENCE_GAIN = 0.001;

// Excitation shaping. A raw noise burst drives every harmonic equally hard, which sounds thin
// and buzzy; the target here is a classical guitar's fingerstyle pluck — flesh on nylon, out
// near the soundhole. The comb filter cancels harmonics with a node at the pluck position (and
// removes DC); the one-pole lowpass rounds the attack the way a fingertip does.
const PLUCK_POSITION = 0.28; // fraction of string length from the bridge
const EXCITATION_LOWPASS = 0.35; // one-pole coefficient; higher = brighter attack
// The lowpass strips proportionally more energy from high-pitched notes (all their harmonics
// sit up where it cuts), so every pluck is normalized to this RMS to keep notes evenly loud.
const EXCITATION_RMS = 0.35;
// A fingertip release is not instantaneous — a few milliseconds of ramp softens the onset.
const ATTACK_SECONDS = 0.004;

// Each pluck is doubled by a quieter voice, slightly detuned and delayed — the natural chorus
// of a real instrument's paired strings, which fills out the otherwise sterile single voice.
const DOUBLE_DETUNE_CENTS = 5;
const DOUBLE_GAIN_RATIO = 0.5;
const DOUBLE_DELAY_SECONDS = 0.009;

export function getFrequency(stringNum: number, fretNum: number): number {
  const openFreq = OPEN_STRING_FREQUENCIES[stringNum];
  return openFreq * Math.pow(2, fretNum / SEMITONES_PER_OCTAVE);
}

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  // Resume if suspended (browser autoplay policy)
  if (audioCtx.state === "suspended") {
    audioCtx.resume();
  }
  return audioCtx;
}

// Shared "guitar body" output chain: a warmth boost near the body's air resonance, a cut in the
// honky mids that make raw Karplus-Strong sound nasal, a shelf to tame synthetic fizz, and a
// compressor so notes tapped in quick succession ring together instead of clipping. After it,
// the user's volume as a master gain, then a limiter: the volume's top end pushes peaks past
// full scale, and a brick-wall stage turns what would be hard clipping into a brief squash.
interface OutputChain {
  input: AudioNode;
  masterGain: GainNode;
}
let outputChain: OutputChain | null = null;

function getOutputChain(ctx: AudioContext): OutputChain {
  if (!outputChain) {
    // A shelf rather than a peak so the lowest fundamentals (low E is 82 Hz) get the full
    // boost. The corner sits above the A string's range so notes there get the same warmth as
    // the low E — fundamental and second harmonic both lifted — and D-string notes get a
    // partial lift, keeping the whole bass register full.
    const warmth = new BiquadFilterNode(ctx, { type: "lowshelf", frequency: 280, gain: 6 });
    const mids = new BiquadFilterNode(ctx, { type: "peaking", frequency: 900, Q: 0.9, gain: -4 });
    const fizz = new BiquadFilterNode(ctx, { type: "highshelf", frequency: 3800, gain: -6 });
    const glue = new DynamicsCompressorNode(ctx, {
      threshold: -18,
      knee: 12,
      ratio: 3,
      attack: 0.003,
      release: 0.25,
    });
    const masterGain = new GainNode(ctx, { gain: gainForVolume(DEFAULT_VOLUME) });
    const limiter = new DynamicsCompressorNode(ctx, {
      threshold: -1,
      knee: 0,
      ratio: 20,
      attack: 0.001,
      release: 0.1,
    });
    warmth
      .connect(mids)
      .connect(fizz)
      .connect(glue)
      .connect(masterGain)
      .connect(limiter)
      .connect(ctx.destination);
    outputChain = { input: warmth, masterGain };
  }
  return outputChain;
}

// Ramped rather than set outright: a step in gain under a ringing note would click.
const VOLUME_RAMP_SECONDS = 0.02;

function applyVolume(ctx: AudioContext, volume: number): void {
  const { masterGain } = getOutputChain(ctx);
  masterGain.gain.setTargetAtTime(gainForVolume(volume), ctx.currentTime, VOLUME_RAMP_SECONDS);
}

function generateKarplusStrong(
  ctx: AudioContext,
  frequency: number,
  { durationSeconds, decay }: PluckCharacter
): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const totalSamples = Math.floor(sampleRate * durationSeconds);
  const buffer = ctx.createBuffer(1, totalSamples, sampleRate);
  const data = buffer.getChannelData(0);

  // Delay line length determines pitch. The 1-2-1 loop filter below is centered one period
  // back, so its group delay is exactly the delay length — no fractional compensation needed.
  const delayLength = Math.max(3, Math.round(sampleRate / frequency));

  // The "pluck": lowpassed noise, comb-filtered at the pluck position
  const noise = new Float32Array(delayLength);
  let lowpassed = 0;
  for (let i = 0; i < delayLength; i++) {
    lowpassed += EXCITATION_LOWPASS * (Math.random() * 2 - 1 - lowpassed);
    noise[i] = lowpassed;
  }
  const pluckDelay = Math.max(1, Math.round(delayLength * PLUCK_POSITION));
  let energy = 0;
  for (let i = 0; i < delayLength; i++) {
    data[i] = noise[i] - (i >= pluckDelay ? noise[i - pluckDelay] : 0);
    energy += data[i] * data[i];
  }
  const normalize = EXCITATION_RMS / (Math.sqrt(energy / delayLength) || 1);
  for (let i = 0; i < delayLength; i++) {
    data[i] *= normalize;
  }

  // Karplus-Strong: each sample = a 1-2-1 weighted average centered one period ago, with a
  // small decay factor for natural string damping. The three-tap average damps upper
  // harmonics faster than the classic two-tap — nylon strings shed brightness quickly,
  // where steel keeps ringing metallic.
  for (let i = delayLength; i < totalSamples; i++) {
    const behind = i - delayLength;
    data[i] =
      decay * 0.25 * ((behind > 0 ? data[behind - 1] : 0) + 2 * data[behind] + data[behind + 1]);
  }

  return buffer;
}

function startVoice(
  ctx: AudioContext,
  buffer: AudioBuffer,
  when: number,
  peakGain: number,
  detuneCents: number
): void {
  const source = new AudioBufferSourceNode(ctx, { buffer, detune: detuneCents });
  // Fingertip attack ramp, then a gentle fade to silence over the buffer's length
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, when);
  gain.gain.linearRampToValueAtTime(peakGain, when + ATTACK_SECONDS);
  gain.gain.exponentialRampToValueAtTime(SILENCE_GAIN, when + buffer.duration);
  source.connect(gain);
  gain.connect(getOutputChain(ctx).input);
  source.start(when);
  source.stop(when + buffer.duration);
}

interface PlayOptions {
  letRing?: boolean;
  volume?: number;
}

export function playNote(
  stringNum: number,
  fretNum: number,
  { letRing = false, volume = DEFAULT_VOLUME }: PlayOptions = {}
): void {
  const ctx = getAudioContext();
  applyVolume(ctx, volume);
  const frequency = getFrequency(stringNum, fretNum);
  const buffer = generateKarplusStrong(ctx, frequency, letRing ? RINGING_PLUCK : PLUCK);

  startVoice(ctx, buffer, ctx.currentTime, PLUCK_GAIN, 0);
  startVoice(
    ctx,
    buffer,
    ctx.currentTime + DOUBLE_DELAY_SECONDS,
    PLUCK_GAIN * DOUBLE_GAIN_RATIO,
    DOUBLE_DETUNE_CENTS
  );
}
